import { Image, ImageProps } from "./Image";
import { ReactNode } from "react";
import { ImageSizes } from "@ui/lib/image";
import { cva, VariantProps } from "class-variance-authority";
import { Link, LinkProps } from "@/i18n/navigation";
import { cn } from "@ui/lib/utils";
const categoryCardRootVariants = cva("flex items-center justify-between", {
  variants: {
    linkProps: {
      true: "cursor-pointer relative group",
      false: "cursor-default ",
    },
    variant: {
      ghost: "",
      outline: "rounded-md border bg-transparent p-4 shadow-lg transition-colors lg:p-8",
    },
  },
  defaultVariants: {
    linkProps: false,
    variant: "ghost",
  },
});

export type CategoryCardRootVariants = VariantProps<typeof categoryCardRootVariants>;
export type CategoryCardRootProps = Omit<CategoryCardRootVariants, "linkProps"> & {
  children: ReactNode;
  className?: string;
  linkProps?: LinkProps;
} & React.HTMLAttributes<HTMLDivElement>;

export function CategoryCardRoot({ children, className, linkProps, variant, ...rest }: CategoryCardRootProps) {
  const Tag: any = linkProps ? Link : "div";
  return (
    <Tag
      className={cn(categoryCardRootVariants({ linkProps: !!linkProps, variant }), className)}
      {...(linkProps ? linkProps : rest)}
    >
      {children}
    </Tag>
  );
}

const categoryCardBodyVariants = cva("flex max-w-sm items-center gap-x-8 flex-1 min-w-0", {
  variants: {
    maxWidth: {
      xs: "max-w-xs",
      default: "max-w-sm",
    },
    defaultVariants: {
      maxWidth: "default",
    },
  },
});
export type CategoryCardVariants = VariantProps<typeof categoryCardBodyVariants>;
export type CategoryCardProps = CategoryCardVariants & {
  imageProps?: Pick<ImageProps, "resource">;
  title: string;
  description: ReactNode;
};

export function CategoryCardBody({ imageProps, title, description, maxWidth }: CategoryCardProps) {
  return (
    <div className={categoryCardBodyVariants({ maxWidth })}>
      {imageProps && (
        <Image className='aspect-1x1 size-40 rounded-md' sizes={ImageSizes.CONTENT_CARDS} {...imageProps} />
      )}
      <div className='min-w-0 space-y-4 truncate'>
        <h3 className='tw-type-base tw-font-bold tw-leading-2xl truncate'>{title}</h3>
        <div className='f-paragraph-04 flex items-center gap-x-4'>{description}</div>
      </div>
    </div>
  );
}
